import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface FeatureFlags {
  // Router Migration Flags - Sircon Business Areas
  useAngularRouter: boolean;
  useAngularRouterAuth: boolean;           // Login, SSO, password flows
  useAngularRouterNetwork: boolean;        // Network entity management
  useAngularRouterDashboard: boolean;      // Activity, compliance dashboards
  useAngularRouterProfile: boolean;        // User profile management
  useAngularRouterAdmin: boolean;          // Admin console, user management
  useAngularRouterWorkflows: boolean;      // Filing, interview workflows
  useAngularRouterContracts: boolean;      // Producer contracts, carrier contracts
  useAngularRouterSignup: boolean;         // Registration, invitation flows

  // Sircon-Specific Feature Areas
  useAngularRouterEntityRecord: boolean;   // Entity record nested routes
  useAngularRouterCarrierProfile: boolean; // Carrier-specific functionality
  useAngularRouterAgencyProfile: boolean;  // Agency management
  useAngularRouterCompliance: boolean;     // Compliance dashboards
  useAngularRouterReports: boolean;        // Reporting functionality
  useAngularRouterDocuments: boolean;      // Document management

  // Migration Safety Flags
  enableRoutePerformanceMonitoring: boolean;
  enableRouteLogging: boolean;
  enableFallbackToUIRouter: boolean;
  enableRouteValidation: boolean;
  enableUserContextPreloading: boolean;    // Sircon-specific: preload user context
  enablePermissionCaching: boolean;        // Cache permission checks
  enableNetworkRouteOptimization: boolean; // Optimize network entity routes

  // Development & Testing Flags
  showMigrationDebugInfo: boolean;
  enableRouteComparison: boolean;
  logRouteTransitions: boolean;
  mockUserContextForTesting: boolean;      // For testing without full auth
  enableRouteTimingAnalysis: boolean;      // Detailed timing breakdowns
}

const DEFAULT_FLAGS: FeatureFlags = {
  // Router Migration Flags - Enabled for full-scale testing
  useAngularRouter: true,
  useAngularRouterAuth: true,
  useAngularRouterNetwork: true,
  useAngularRouterDashboard: true,
  useAngularRouterProfile: true,
  useAngularRouterAdmin: true,
  useAngularRouterWorkflows: true,
  useAngularRouterContracts: true,
  useAngularRouterSignup: true,

  // Sircon-Specific Feature Areas - Enabled for full-scale testing
  useAngularRouterEntityRecord: true,
  useAngularRouterCarrierProfile: true,
  useAngularRouterAgencyProfile: true,
  useAngularRouterCompliance: true,
  useAngularRouterReports: true,
  useAngularRouterDocuments: true,

  // Migration Safety Flags - Enable monitoring by default
  enableRoutePerformanceMonitoring: true,
  enableRouteLogging: true,
  enableFallbackToUIRouter: true,
  enableRouteValidation: true,
  enableUserContextPreloading: true,
  enablePermissionCaching: true,
  enableNetworkRouteOptimization: true,

  // Development Flags - Enable for testing phase
  showMigrationDebugInfo: true,
  enableRouteComparison: true,
  logRouteTransitions: true,
  mockUserContextForTesting: false,
  enableRouteTimingAnalysis: true
};

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {
  private readonly STORAGE_KEY = 'sircon-feature-flags';
  private flagsSubject = new BehaviorSubject<FeatureFlags>(DEFAULT_FLAGS);

  constructor() {
    this.loadFlags();
    this.applyEnvironmentOverrides();
  }

  /**
   * Get current feature flags as observable
   */
  getFlags(): Observable<FeatureFlags> {
    return this.flagsSubject.asObservable();
  }

  /**
   * Get current feature flags synchronously
   */
  getCurrentFlags(): FeatureFlags {
    return this.flagsSubject.value;
  }

  /**
   * Check if a specific feature is enabled
   */
  isEnabled(flagName: keyof FeatureFlags): boolean {
    return this.flagsSubject.value[flagName] as boolean;
  }

  /**
   * Enable a specific feature flag
   */
  enable(flagName: keyof FeatureFlags): void {
    this.updateFlag(flagName, true);
  }

  /**
   * Disable a specific feature flag
   */
  disable(flagName: keyof FeatureFlags): void {
    this.updateFlag(flagName, false);
  }

  /**
   * Toggle a specific feature flag
   */
  toggle(flagName: keyof FeatureFlags): void {
    const currentValue = this.flagsSubject.value[flagName] as boolean;
    this.updateFlag(flagName, !currentValue);
  }

  /**
   * Update a specific flag
   */
  private updateFlag(flagName: keyof FeatureFlags, value: boolean): void {
    const currentFlags = this.flagsSubject.value;
    const newFlags = { ...currentFlags, [flagName]: value };
    this.flagsSubject.next(newFlags);
    this.saveFlags(newFlags);
  }

  /**
   * Update multiple flags at once
   */
  updateFlags(updates: Partial<FeatureFlags>): void {
    const currentFlags = this.flagsSubject.value;
    const newFlags = { ...currentFlags, ...updates };
    this.flagsSubject.next(newFlags);
    this.saveFlags(newFlags);
  }

  /**
   * Reset all flags to defaults
   */
  resetToDefaults(): void {
    const defaultFlags = { ...DEFAULT_FLAGS };
    this.applyEnvironmentOverrides(defaultFlags);
    this.flagsSubject.next(defaultFlags);
    this.saveFlags(defaultFlags);
  }

  /**
   * Get router type for a specific route category
   */
  getRouterTypeForCategory(category: string): 'ui-router' | 'angular-router' {
    const flags = this.getCurrentFlags();

    // If global Angular Router is disabled, use UI-Router
    if (!flags.useAngularRouter) {
      return 'ui-router';
    }

    // Check category-specific flags
    switch (category) {
      case 'auth':
      case 'authentication':
        return flags.useAngularRouterAuth ? 'angular-router' : 'ui-router';

      case 'network':
        return flags.useAngularRouterNetwork ? 'angular-router' : 'ui-router';

      case 'dashboard':
        return flags.useAngularRouterDashboard ? 'angular-router' : 'ui-router';

      case 'profile':
        return flags.useAngularRouterProfile ? 'angular-router' : 'ui-router';

      case 'admin':
        return flags.useAngularRouterAdmin ? 'angular-router' : 'ui-router';

      case 'workflow':
      case 'workflows':
        return flags.useAngularRouterWorkflows ? 'angular-router' : 'ui-router';

      case 'contract':
      case 'contracts':
        return flags.useAngularRouterContracts ? 'angular-router' : 'ui-router';

      case 'signup':
        return flags.useAngularRouterSignup ? 'angular-router' : 'ui-router';

      default:
        // For unknown categories, use UI-Router as fallback
        return 'ui-router';
    }
  }

  /**
   * Check if Angular Router should be used for a specific route (Sircon-specific logic)
   */
  shouldUseAngularRouter(routeName: string): boolean {
    const flags = this.getCurrentFlags();

    // Global flag check
    if (!flags.useAngularRouter) {
      return false;
    }

    // Authentication routes
    if (this.isAuthRoute(routeName)) {
      return flags.useAngularRouterAuth;
    }

    // Network routes (most complex in Sircon)
    if (this.isNetworkRoute(routeName)) {
      // Check for sub-category flags
      if (routeName.includes('entity')) {
        return flags.useAngularRouterEntityRecord && flags.useAngularRouterNetwork;
      }
      if (routeName.includes('agencyProfile')) {
        return flags.useAngularRouterAgencyProfile && flags.useAngularRouterNetwork;
      }
      if (routeName.includes('carrierProfile')) {
        return flags.useAngularRouterCarrierProfile && flags.useAngularRouterNetwork;
      }
      return flags.useAngularRouterNetwork;
    }

    // Dashboard routes
    if (this.isDashboardRoute(routeName)) {
      if (routeName.includes('compliance')) {
        return flags.useAngularRouterCompliance && flags.useAngularRouterDashboard;
      }
      return flags.useAngularRouterDashboard;
    }

    // Profile routes
    if (routeName.startsWith('profile')) {
      return flags.useAngularRouterProfile;
    }

    // Admin/Configuration routes
    if (this.isAdminRoute(routeName)) {
      return flags.useAngularRouterAdmin;
    }

    // Workflow routes (complex multi-step processes)
    if (this.isWorkflowRoute(routeName)) {
      return flags.useAngularRouterWorkflows;
    }

    // Contract routes
    if (this.isContractRoute(routeName)) {
      return flags.useAngularRouterContracts;
    }

    // Signup/Invitation routes
    if (this.isSignupRoute(routeName)) {
      return flags.useAngularRouterSignup;
    }

    // Reports routes
    if (this.isReportRoute(routeName)) {
      return flags.useAngularRouterReports;
    }

    // Document routes
    if (this.isDocumentRoute(routeName)) {
      return flags.useAngularRouterDocuments;
    }

    // Default to UI-Router for unknown routes
    return false;
  }

  // Sircon-specific route identification helpers
  private isAuthRoute(routeName: string): boolean {
    const authPatterns = [
      'login', 'auth', 'sso', 'forgot', 'reset', 'expired',
      'agencyLogin', 'authenticate', 'signInHelp'
    ];
    return authPatterns.some(pattern => routeName.includes(pattern));
  }

  private isNetworkRoute(routeName: string): boolean {
    return routeName.startsWith('network');
  }

  private isDashboardRoute(routeName: string): boolean {
    return routeName.startsWith('dashboard');
  }

  private isAdminRoute(routeName: string): boolean {
    const adminPatterns = ['admin', 'configuration', 'users', 'settings', 'editWorkflow'];
    return adminPatterns.some(pattern => routeName.includes(pattern));
  }

  private isWorkflowRoute(routeName: string): boolean {
    const workflowPatterns = [
      'workflow', 'filing', 'interview', 'carrierConnect', 'onboarding',
      'contactChange', 'licensePrint', 'license-print', 'bulkRenewal', 'associations'
    ];
    return workflowPatterns.some(pattern => routeName.includes(pattern));
  }

  private isContractRoute(routeName: string): boolean {
    const contractPatterns = [
      'contract', 'producerContract', 'carrierContract', 'createProducerContract',
      'updateProducerContract', 'contractConfiguration'
    ];
    return contractPatterns.some(pattern => routeName.includes(pattern));
  }

  private isSignupRoute(routeName: string): boolean {
    const signupPatterns = ['signup', 'invite', 'activation', 'acceptInvitation'];
    return signupPatterns.some(pattern => routeName.includes(pattern));
  }

  private isReportRoute(routeName: string): boolean {
    const reportPatterns = ['report', 'registrationsDashboard', 'allActivity'];
    return reportPatterns.some(pattern => routeName.includes(pattern));
  }

  private isDocumentRoute(routeName: string): boolean {
    const documentPatterns = [
      'viewDocument', 'viewContractFile', 'documentShared',
      'carrierDocuments', 'producerDocuments'
    ];
    return documentPatterns.some(pattern => routeName.includes(pattern));
  }

  /**
   * Enable progressive migration - enable flags in order of migration phases
   */
  enableProgressiveMigration(phase: number): void {
    const updates: Partial<FeatureFlags> = {};

    // Phase 1: Core infrastructure
    if (phase >= 1) {
      updates.useAngularRouter = true;
      updates.enableRoutePerformanceMonitoring = true;
      updates.enableRouteLogging = true;
    }

    // Phase 2: Authentication
    if (phase >= 2) {
      updates.useAngularRouterAuth = true;
    }

    // Phase 3: Network and Dashboard
    if (phase >= 3) {
      updates.useAngularRouterNetwork = true;
      updates.useAngularRouterDashboard = true;
      updates.useAngularRouterProfile = true;
    }

    // Phase 4: Admin
    if (phase >= 4) {
      updates.useAngularRouterAdmin = true;
    }

    // Phase 5: Complex workflows
    if (phase >= 5) {
      updates.useAngularRouterWorkflows = true;
      updates.useAngularRouterContracts = true;
      updates.useAngularRouterSignup = true;
    }

    this.updateFlags(updates);
  }

  /**
   * Get migration progress as percentage
   */
  getMigrationProgress(): number {
    const flags = this.getCurrentFlags();
    const migrationFlags = [
      flags.useAngularRouterAuth,
      flags.useAngularRouterNetwork,
      flags.useAngularRouterDashboard,
      flags.useAngularRouterProfile,
      flags.useAngularRouterAdmin,
      flags.useAngularRouterWorkflows,
      flags.useAngularRouterContracts,
      flags.useAngularRouterSignup
    ];

    const enabledCount = migrationFlags.filter(flag => flag).length;
    return (enabledCount / migrationFlags.length) * 100;
  }

  /**
   * Load flags from localStorage
   */
  private loadFlags(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const storedFlags = JSON.parse(stored);
        // Merge with defaults to handle new flags
        const mergedFlags = { ...DEFAULT_FLAGS, ...storedFlags };
        this.flagsSubject.next(mergedFlags);
      }
    } catch (error) {
      console.warn('Failed to load feature flags from storage:', error);
    }
  }

  /**
   * Save flags to localStorage
   */
  private saveFlags(flags: FeatureFlags): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(flags));
    } catch (error) {
      console.warn('Failed to save feature flags to storage:', error);
    }
  }

  /**
   * Apply environment-specific overrides
   */
  private applyEnvironmentOverrides(flags?: FeatureFlags): void {
    const targetFlags = flags || this.flagsSubject.value;

    // Check for environment variables or build-time flags
    // This would typically be set during build process
    if (typeof window !== 'undefined' && (window as any).SIRCON_FEATURE_FLAGS) {
      const envFlags = (window as any).SIRCON_FEATURE_FLAGS;
      Object.assign(targetFlags, envFlags);
    }

    // Development environment overrides
    if (this.isDevelopmentMode()) {
      targetFlags.showMigrationDebugInfo = true;
      targetFlags.enableRouteComparison = true;
      targetFlags.logRouteTransitions = true;
    }

    if (!flags) {
      this.flagsSubject.next(targetFlags);
      this.saveFlags(targetFlags);
    }
  }

  /**
   * Check if we're in development mode
   */
  private isDevelopmentMode(): boolean {
    return !!(typeof window !== 'undefined' &&
             (window.location.hostname === 'localhost' ||
              window.location.hostname === '127.0.0.1' ||
              window.location.protocol === 'http:'));
  }

  /**
   * Export current flags configuration
   */
  exportConfiguration(): string {
    const flags = this.getCurrentFlags();
    const config = {
      timestamp: new Date().toISOString(),
      migrationProgress: this.getMigrationProgress(),
      flags: flags
    };
    return JSON.stringify(config, null, 2);
  }

  /**
   * Import flags configuration
   */
  importConfiguration(configJson: string): boolean {
    try {
      const config = JSON.parse(configJson);
      if (config.flags && typeof config.flags === 'object') {
        // Validate that the imported config has valid flag structure
        const validFlags = Object.keys(DEFAULT_FLAGS);
        const importedFlags: Partial<FeatureFlags> = {};

        validFlags.forEach(key => {
          if (key in config.flags) {
            importedFlags[key as keyof FeatureFlags] = config.flags[key];
          }
        });

        this.updateFlags(importedFlags);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to import feature flag configuration:', error);
      return false;
    }
  }
}